import { CodeBlock, TabbedCodeBlock } from '../../components/developers/CodeBlock';

export default function GettingStarted() {
  return (
    <article className="prose prose-slate max-w-none">
      <h1>Getting Started</h1>
      <p className="lead">
        Learn how to set up your environment and make your first API call to the Opair AI Governance
        Platform.
      </p>

      <h2>Prerequisites</h2>
      <ul>
        <li>Node.js 18 or later</li>
        <li>An Opair account with API access</li>
        <li>Basic understanding of REST APIs</li>
      </ul>

      <h2>Installation</h2>
      <p>Install the Opair SDK and CLI tools:</p>

      <TabbedCodeBlock
        tabs={[
          { label: 'npm', code: 'npm install @opair/sdk @opair/cli', language: 'bash' },
          { label: 'pnpm', code: 'pnpm add @opair/sdk @opair/cli', language: 'bash' },
          { label: 'yarn', code: 'yarn add @opair/sdk @opair/cli', language: 'bash' },
        ]}
      />

      <h2>Configuration</h2>
      <p>Create a configuration file for your project:</p>

      <CodeBlock
        title="opair.config.ts"
        language="typescript"
        code={`import { defineConfig } from '@opair/sdk';

export default defineConfig({
  apiKey: process.env.OPAIR_API_KEY,
  baseUrl: 'https://api.opair.example.com',
  organizationId: 'your-org-id',
});`}
      />

      <h2>Your First API Call</h2>
      <p>Make a simple health check to verify your configuration:</p>

      <CodeBlock
        title="example.ts"
        language="typescript"
        code={`import { OpairClient } from '@opair/sdk';

const client = new OpairClient({
  apiKey: process.env.OPAIR_API_KEY!,
});

async function checkHealth() {
  const health = await client.health.check();
  console.log('API Status:', health.status);
}

checkHealth();`}
      />

      <h2>Environment Variables</h2>
      <p>We recommend storing sensitive values in environment variables:</p>

      <CodeBlock
        title=".env"
        language="bash"
        code={`OPAIR_API_KEY=your-api-key-here
OPAIR_ORG_ID=your-organization-id`}
      />

      <div className="not-prose my-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">Next Steps</h3>
        <ul className="text-blue-800 space-y-1">
          <li>
            Learn about{' '}
            <a href="/developers/authentication" className="text-blue-600 hover:underline">
              Authentication
            </a>
          </li>
          <li>
            Follow the{' '}
            <a href="/developers/sdk-quickstart" className="text-blue-600 hover:underline">
              SDK Quickstart
            </a>
          </li>
          <li>
            Explore the{' '}
            <a href="/developers/api-reference" className="text-blue-600 hover:underline">
              API Reference
            </a>
          </li>
        </ul>
      </div>
    </article>
  );
}
